import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGoogleMapsApiKey } from "../../../../../commons/googleMaps/googleMapsKeyContext";
import GoogleMapFrameHost from "../../../../../commons/googleMaps/googleMapFrame/GoogleMapFrameHost";
import { StyledGoogleMaps } from "../../../../../styled/googleMaps";
import {
  catalogOptionLabel,
  locationFromCoords,
} from "../../../../forms/fptTraslados/fptTrasladosUtils";
import { AGENCIAS_FRAME_PATH } from "../agenciasFramePath";
import LegendItem from "./LegendItem";
import { ARGENTINA_CENTER, PIN_STYLES } from "./mapConstants";

const getAgenciaLocation = (agencia) =>
  locationFromCoords(
    agencia?.latitud ?? agencia?.Latitud,
    agencia?.longitud ?? agencia?.Longitud,
  );

const areCoordsEqual = (left, right) =>
  left?.lat === right?.lat && left?.lng === right?.lng;

const AgenciasAsignacionMap = ({
  agencias = [],
  idArt,
  selectedAgenciaId,
  origen,
  destino,
  origenLabel,
  destinoLabel,
  onSelectAgencia,
  interactive = true,
}) => {
  const [localSelectedId, setLocalSelectedId] = useState(() =>
    selectedAgenciaId != null ? String(selectedAgenciaId) : "",
  );
  const onSelectRef = useRef(onSelectAgencia);
  const { apiKey, failed: keyFailed } = useGoogleMapsApiKey(idArt);
  onSelectRef.current = onSelectAgencia;

  useEffect(() => {
    setLocalSelectedId(
      selectedAgenciaId != null ? String(selectedAgenciaId) : "",
    );
  }, [selectedAgenciaId]);

  const handleSelectAgencia = useCallback((id) => {
    setLocalSelectedId(String(id));
    onSelectRef.current?.(id);
  }, []);

  const selectHandler = interactive ? handleSelectAgencia : null;

  const origenLocation = useMemo(
    () => locationFromCoords(origen?.lat, origen?.lng),
    [origen?.lat, origen?.lng],
  );
  const destinoLocation = useMemo(
    () => locationFromCoords(destino?.lat, destino?.lng),
    [destino?.lat, destino?.lng],
  );

  const agenciaMarkers = useMemo(
    () =>
      (agencias || [])
        .map((agencia) => {
          const location = getAgenciaLocation(agencia);
          if (!location) return null;
          return {
            id: agencia.id,
            label: catalogOptionLabel(agencia),
            location,
          };
        })
        .filter(Boolean),
    [agencias],
  );

  const endpointLocations = useMemo(() => {
    const locations = [];
    if (origenLocation) locations.push(origenLocation);
    if (destinoLocation) locations.push(destinoLocation);
    return locations;
  }, [destinoLocation, origenLocation]);

  const allLocations = useMemo(() => {
    const locations = [...endpointLocations];
    agenciaMarkers.forEach((marker) => locations.push(marker.location));
    return locations;
  }, [agenciaMarkers, endpointLocations]);

  const initialCenter = endpointLocations[0] || allLocations[0] || ARGENTINA_CENTER;
  const initialZoom = endpointLocations.length > 1 ? 10 : 14;
  const payload = useMemo(
    () => ({
      agenciaMarkers,
      destinoLabel,
      destinoLocation,
      initialCenter,
      initialZoom,
      interactive,
      origenLabel,
      origenLocation,
      selectedAgenciaId: localSelectedId,
      viewportLocations: endpointLocations,
    }),
    [
      agenciaMarkers,
      destinoLabel,
      destinoLocation,
      endpointLocations,
      initialCenter,
      initialZoom,
      interactive,
      localSelectedId,
      origenLabel,
      origenLocation,
    ],
  );

  if (allLocations.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Sin coordenadas para mostrar en el mapa.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: "#5b9bf5" }}>
        Mapa de asignacion
      </Typography>
      {apiKey ? (
        <GoogleMapFrameHost
          apiKey={apiKey}
          onMessage={(data) => {
            if (data?.type === "select") selectHandler?.(data.idAgencia);
          }}
          payload={payload}
          route={AGENCIAS_FRAME_PATH}
          title="Mapa de asignacion"
        />
      ) : keyFailed ? (
        <Alert severity="warning">
          No se pudo obtener la clave de Google Maps.
        </Alert>
      ) : (
        <StyledGoogleMaps.MapContainer
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </StyledGoogleMaps.MapContainer>
      )}
      <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap", alignItems: "center" }}>
        <LegendItem pinStyle={PIN_STYLES.origen} label="Inicio" />
        <LegendItem pinStyle={PIN_STYLES.destino} label="Fin" />
        <LegendItem
          pinStyle={PIN_STYLES.agencia}
          label={
            interactive
              ? `Agencia${localSelectedId ? " (verde = seleccionada)" : " (click para seleccionar)"}`
              : "Agencia"
          }
        />
      </Box>
    </Box>
  );
};

export default memo(AgenciasAsignacionMap, (prev, next) =>
  prev.interactive === next.interactive &&
  prev.idArt === next.idArt &&
  prev.origenLabel === next.origenLabel &&
  prev.destinoLabel === next.destinoLabel &&
  prev.selectedAgenciaId === next.selectedAgenciaId &&
  areCoordsEqual(prev.origen, next.origen) &&
  areCoordsEqual(prev.destino, next.destino) &&
  prev.agencias === next.agencias &&
  prev.onSelectAgencia === next.onSelectAgencia,
);
